import type Phaser from 'phaser';

export interface PlayerCardOptions {
  scene: Phaser.Scene;
  /** Card rectangle in canvas-buffer pixels. */
  rect: { x: number; y: number; w: number; h: number };
  /** Multiplier from intended-CSS-px to canvas-buffer-px (= devicePixelRatio). */
  dpr: number;
  /** Brand text — defaults to "Casino". */
  brandText?: string;
  /**
   * Primary button (gold). Common label: "TAKE DOWN" (craps). Pass `null`
   * to hide.
   */
  primaryButton?: { label: string; onClick: () => void } | null;
  /** Secondary button (red ghost). Pass `null` to hide. */
  secondaryButton?: { label: string; onClick: () => void } | null;
  /** Optional checkbox-style toggles rendered between bankroll and buttons. */
  toggles?: Array<{ label: string; initial: boolean; onChange: (value: boolean) => void }>;
  /** Player's tier — drives the badge in the card's top-right corner. */
  tier?: 'standard' | 'high-limit' | 'whale';
}

/**
 * Vertical player card — matches the craps cardVertical inline rendering so
 * the lobby and craps table both show the same widget at the
 * same position. Brand on top, divider, EMV chip, BANKROLL label + value,
 * stacked pill buttons, MEMBER footer.
 */
export class PlayerCard {
  private bankrollText!: Phaser.GameObjects.Text;
  private objects: Phaser.GameObjects.GameObject[] = [];

  constructor(opts: PlayerCardOptions) {
    this.draw(opts);
  }

  private track<T extends Phaser.GameObjects.GameObject>(o: T): T {
    this.objects.push(o);
    return o;
  }

  private draw(opts: PlayerCardOptions) {
    const { scene, rect: r, dpr } = opts;
    const fpx = (n: number) => Math.round(n * dpr);

    const cardR = Math.max(fpx(6), Math.round(Math.min(r.w, r.h) * 0.06));
    const padX = Math.round(r.w * 0.1);

    // Drop shadow.
    const shadow = this.track(scene.add.graphics());
    shadow.fillStyle(0x000000, 0.5);
    shadow.fillRoundedRect(r.x + 2, r.y + 4, r.w, r.h, cardR);

    // Card body — dark navy with sheen + gold border + inset thin gold stroke.
    const body = this.track(scene.add.graphics());
    body.fillStyle(0x101a26, 1);
    body.fillRoundedRect(r.x, r.y, r.w, r.h, cardR);
    body.fillStyle(0x1a2538, 0.55);
    body.fillRoundedRect(r.x, r.y, r.w, r.h * 0.45, cardR);
    body.lineStyle(fpx(2), 0xd4af37, 0.9);
    body.strokeRoundedRect(r.x, r.y, r.w, r.h, cardR);
    body.lineStyle(fpx(1), 0xd4af37, 0.22);
    body.strokeRoundedRect(
      r.x + fpx(3),
      r.y + fpx(3),
      r.w - fpx(6),
      r.h - fpx(6),
      Math.max(2, cardR - fpx(2)),
    );

    // Brand top-left.
    this.track(
      scene.add
        .text(r.x + padX, r.y + fpx(8), opts.brandText ?? 'Casino', {
          fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
          fontSize: `${fpx(10)}px`,
          color: '#d4af37',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0),
    );

    // Tier top-right.
    const tierBadge = TIER_BADGE[opts.tier ?? 'standard'];
    this.track(
      scene.add
        .text(r.x + r.w - padX, r.y + fpx(9), tierBadge.text, {
          fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
          fontSize: `${fpx(7)}px`,
          color: tierBadge.color,
          fontStyle: 'bold',
        })
        .setOrigin(1, 0),
    );

    // Gold divider beneath brand strip.
    const dividerY = r.y + fpx(22);
    const divider = this.track(scene.add.graphics());
    divider.lineStyle(fpx(1), 0xd4af37, 0.28);
    divider.lineBetween(r.x + padX, dividerY, r.x + r.w - padX, dividerY);

    // EMV chip graphic.
    const chipW = fpx(28);
    const chipH = fpx(20);
    const chipX = r.x + padX;
    const chipY = r.y + fpx(28);
    const chipGfx = this.track(scene.add.graphics());
    chipGfx.fillStyle(0xc9a23a, 1);
    chipGfx.fillRoundedRect(chipX, chipY, chipW, chipH, fpx(2));
    chipGfx.fillStyle(0xe6c454, 1);
    chipGfx.fillRoundedRect(chipX + 1, chipY + 1, chipW - 2, chipH * 0.4, Math.max(1, fpx(1.5)));
    chipGfx.lineStyle(fpx(1), 0x6e5613, 0.85);
    chipGfx.strokeRoundedRect(chipX, chipY, chipW, chipH, fpx(2));
    chipGfx.lineStyle(fpx(1), 0x6e5613, 0.7);
    chipGfx.lineBetween(
      chipX + chipW * 0.3,
      chipY + chipH * 0.15,
      chipX + chipW * 0.3,
      chipY + chipH * 0.85,
    );
    chipGfx.lineBetween(
      chipX + chipW * 0.7,
      chipY + chipH * 0.15,
      chipX + chipW * 0.7,
      chipY + chipH * 0.85,
    );
    chipGfx.lineBetween(
      chipX + chipW * 0.15,
      chipY + chipH * 0.5,
      chipX + chipW * 0.85,
      chipY + chipH * 0.5,
    );

    // BANKROLL label + value below the chip.
    const labelY = chipY + chipH + fpx(8);
    this.track(
      scene.add
        .text(r.x + padX, labelY, 'BANKROLL', {
          fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
          fontSize: `${fpx(7)}px`,
          color: '#9aa3ad',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0),
    );
    this.bankrollText = this.track(
      scene.add
        .text(r.x + padX, labelY + fpx(10), '$0', {
          fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
          fontSize: `${fpx(20)}px`,
          color: '#f4d35e',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0)
        .setShadow(0, 1, 'rgba(0,0,0,0.55)', 2, false, true),
    );

    // Toggle rows — slot below the bankroll value, above the buttons.
    if (opts.toggles && opts.toggles.length > 0) {
      const togglesY = labelY + fpx(40);
      this.drawToggles(opts, togglesY);
    }

    // Buttons — anchored to the bottom of the card. Primary above secondary,
    // MEMBER footer below them.
    const btnW = r.w - padX * 2;
    const btnH = fpx(30);
    const btnGap = fpx(8);
    const memberY = r.y + r.h - fpx(11);
    const secondaryY = memberY - fpx(8) - btnH;
    const primaryY = secondaryY - btnGap - btnH;
    const btnX = r.x + (r.w - btnW) / 2;

    if (opts.primaryButton) {
      this.makePillButton({
        scene,
        x: btnX,
        y: primaryY,
        w: btnW,
        h: btnH,
        label: opts.primaryButton.label,
        labelColor: '#f4d35e',
        labelFontSize: fpx(12),
        strokeColor: 0xd4af37,
        strokeWidth: fpx(1.2),
        resting: { fillColor: 0x1a2530, fillAlpha: 1, strokeAlpha: 0.85 },
        hover: { fillColor: 0x2a3a4e, fillAlpha: 1, strokeAlpha: 1 },
        pressed: { fillColor: 0x131c25, fillAlpha: 1, strokeAlpha: 1 },
        onClick: opts.primaryButton.onClick,
      });
    }

    if (opts.secondaryButton) {
      this.makePillButton({
        scene,
        x: btnX,
        y: secondaryY,
        w: btnW,
        h: btnH,
        label: opts.secondaryButton.label,
        labelColor: '#e07775',
        labelFontSize: fpx(12),
        strokeColor: 0xc0413f,
        strokeWidth: fpx(1.2),
        resting: { fillColor: 0x000000, fillAlpha: 0, strokeAlpha: 0.9 },
        hover: { fillColor: 0xc0413f, fillAlpha: 0.15, strokeAlpha: 1 },
        pressed: { fillColor: 0xc0413f, fillAlpha: 0.3, strokeAlpha: 1 },
        onClick: opts.secondaryButton.onClick,
      });
    }

    // MEMBER footer.
    this.track(
      scene.add
        .text(r.x + padX, memberY, 'MEMBER · 0001', {
          fontFamily: 'monospace',
          fontSize: `${fpx(7)}px`,
          color: '#5d6776',
        })
        .setOrigin(0, 0),
    );
  }

  private drawToggles(opts: PlayerCardOptions, startY: number) {
    const { scene, rect: r, dpr } = opts;
    const fpx = (n: number) => Math.round(n * dpr);
    const padX = Math.round(r.w * 0.1);
    const rowH = fpx(20);
    const boxSize = fpx(13);
    const boxR = fpx(2);
    const inset = fpx(3);

    for (let i = 0; i < (opts.toggles ?? []).length; i++) {
      const t = opts.toggles![i]!;
      const rowY = startY + i * rowH;
      const boxY = rowY + Math.round((rowH - boxSize) / 2);
      let value = t.initial;

      const box = this.track(scene.add.graphics());
      const drawBox = () => {
        box.clear();
        box.lineStyle(fpx(1.2), 0xd4af37, 0.9);
        box.strokeRoundedRect(r.x + padX, boxY, boxSize, boxSize, boxR);
        if (value) {
          box.fillStyle(0xd4af37, 0.9);
          box.fillRoundedRect(
            r.x + padX + inset,
            boxY + inset,
            boxSize - inset * 2,
            boxSize - inset * 2,
            Math.max(1, boxR - 1),
          );
        }
      };
      drawBox();

      this.track(
        scene.add
          .text(r.x + padX + boxSize + fpx(8), rowY + rowH / 2, t.label, {
            fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
            fontSize: `${fpx(9)}px`,
            color: '#cdd5e0',
          })
          .setOrigin(0, 0.5),
      );

      const hit = this.track(
        scene.add
          .rectangle(r.x + padX, rowY, r.w - padX * 2, rowH, 0x000000, 0)
          .setOrigin(0, 0)
          .setInteractive({ useHandCursor: true }),
      );
      hit.on('pointerdown', () => {
        value = !value;
        drawBox();
        t.onChange(value);
      });
    }
  }

  private makePillButton(opts: {
    scene: Phaser.Scene;
    x: number;
    y: number;
    w: number;
    h: number;
    label: string;
    labelColor: string;
    labelFontSize: number;
    strokeColor: number;
    strokeWidth: number;
    resting: { fillColor: number; fillAlpha: number; strokeAlpha: number };
    hover: { fillColor: number; fillAlpha: number; strokeAlpha: number };
    pressed: { fillColor: number; fillAlpha: number; strokeAlpha: number };
    onClick: () => void;
  }): Phaser.GameObjects.Rectangle {
    const radius = opts.h / 2;
    const bg = this.track(opts.scene.add.graphics());
    const draw = (s: { fillColor: number; fillAlpha: number; strokeAlpha: number }) => {
      bg.clear();
      bg.fillStyle(s.fillColor, s.fillAlpha);
      bg.fillRoundedRect(opts.x, opts.y, opts.w, opts.h, radius);
      bg.lineStyle(opts.strokeWidth, opts.strokeColor, s.strokeAlpha);
      bg.strokeRoundedRect(opts.x, opts.y, opts.w, opts.h, radius);
    };
    draw(opts.resting);
    this.track(
      opts.scene.add
        .text(opts.x + opts.w / 2, opts.y + opts.h / 2, opts.label, {
          fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
          fontSize: `${opts.labelFontSize}px`,
          color: opts.labelColor,
          fontStyle: 'bold',
        })
        .setOrigin(0.5),
    );
    const hit = this.track(
      opts.scene.add
        .rectangle(opts.x + opts.w / 2, opts.y + opts.h / 2, opts.w, opts.h, 0x000000, 0)
        .setInteractive({ useHandCursor: true }),
    );
    hit.on('pointerover', () => draw(opts.hover));
    hit.on('pointerout', () => draw(opts.resting));
    hit.on('pointerdown', () => {
      draw(opts.pressed);
      opts.onClick();
    });
    hit.on('pointerup', () => draw(opts.hover));
    return hit;
  }

  setDepth(depth: number) {
    for (const o of this.objects) {
      const anyObj = o as unknown as { setDepth?: (d: number) => unknown };
      anyObj.setDepth?.(depth);
    }
  }

  destroy() {
    for (const o of this.objects) o.destroy();
    this.objects = [];
  }

  setBankrollCents(cents: number) {
    const dollars = Math.floor(cents / 100);
    this.bankrollText.setText(`$${dollars.toLocaleString()}`);
  }

  /** Top-right corner of the bankroll value (canvas-buffer px) — toast anchor. */
  getBankrollAnchor(): { x: number; y: number } {
    return {
      x: this.bankrollText.x + this.bankrollText.width,
      y: this.bankrollText.y,
    };
  }
}

const TIER_BADGE: Record<NonNullable<PlayerCardOptions['tier']>, { text: string; color: string }> = {
  'standard':   { text: '◆ MEMBER',     color: '#d4af37' },
  'high-limit': { text: '★ HIGH LIMIT', color: '#d4af37' },
  'whale':      { text: '✦ SALON PRIVÉ', color: '#f4d35e' },
};
